/**
 * Collapse every registered manifest's `tracks`/`sfx` lists into one id→channel
 * map.
 *
 * An id appearing in BOTH lists is a manifest bug — it resolves to `music` (the
 * quieter failure: a one-shot played on the music channel is merely mis-mixed,
 * whereas a track played as SFX ignores the music slider entirely) and is
 * reported through `onConflict`.
 */
export function buildChannelMap(sources, onConflict) {
    const map = {};
    for (const { audioManifest } of sources) {
        for (const id of audioManifest.sfx)
            map[id] = 'sfx';
    }
    // Tracks second so music wins a collision without needing a per-id check.
    for (const { audioManifest } of sources) {
        for (const id of audioManifest.tracks) {
            if (map[id] === 'sfx')
                onConflict?.(id);
            map[id] = 'music';
        }
    }
    return map;
}
/**
 * A channel lookup that re-reads its sources on every call, memoising the built
 * map until the source list changes length.
 *
 * Why lazy: `PluginRegistry.register()` runs in `main.tsx` while the
 * composition root is constructed elsewhere, so a map built eagerly at
 * composition time could be built before any game had registered. Resolving on
 * first play removes the ordering coupling entirely.
 */
export function makeChannelResolver(getSources, onConflict) {
    let cached = null;
    let cachedCount = -1;
    return () => {
        const sources = getSources();
        if (cached === null || sources.length !== cachedCount) {
            cached = buildChannelMap(sources, onConflict);
            cachedCount = sources.length;
        }
        return cached;
    };
}
